import { useQuery, useMutation, useQueryClient } from "react-query";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * Notification Platform Phase 2 hooks (SOUND-515) — the per-user in-app inbox +
 * preferences. React Query (the bell polls the unread count), all scoped to the
 * caller. CompanyHeader from useEffectiveCompanyId(); auth from useAuth().
 *
 * Query key prefixes (kept distinct so mark-read invalidates the inbox +
 * unread-count without churning preferences):
 *   notif-unread · notif-list · notif-prefs
 */
const K_UNREAD = "notif-unread";
const K_LIST = "notif-list";
const K_PREFS = "notif-prefs";

function useAuthCtx() {
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const authUser = user ? { id: user.id, accessToken: user.accessToken } : null;
  return { authUser, companyId, ready: !!authUser?.accessToken && !!companyId };
}

/** Bell badge — polls ~60s + refetches on window focus. */
export function useUnreadCount({ enabled = true } = {}) {
  const { authUser, companyId, ready } = useAuthCtx();
  return useQuery(
    K_UNREAD,
    async () => {
      const res = await makeApiRequest(
        "get",
        apiRoutes.notificationUnreadCount,
        authUser,
        companyId
      );
      return res.data?.count ?? 0;
    },
    {
      enabled: enabled && ready,
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    }
  );
}

/** The inbox list — unread-first then newest (server-ordered). */
export function useNotifications({ page = 1, limit = 15, unread = false, enabled = true } = {}) {
  const { authUser, companyId, ready } = useAuthCtx();
  const params = { page, limit, ...(unread ? { unread: "true" } : {}) };
  return useQuery(
    [K_LIST, params],
    async () => {
      const res = await makeApiRequest(
        "get",
        apiRoutes.listNotifications(params),
        authUser,
        companyId
      );
      return res.data || { data: [], pagination: {}, unreadCount: 0 };
    },
    { enabled: enabled && ready, keepPreviousData: true, staleTime: 15_000 }
  );
}

export function useMarkRead() {
  const { authUser, companyId } = useAuthCtx();
  const qc = useQueryClient();
  return useMutation(
    (notificationId) =>
      makeApiRequest("post", apiRoutes.markNotificationRead(notificationId), authUser, companyId),
    {
      onSuccess: () => {
        qc.invalidateQueries(K_UNREAD);
        qc.invalidateQueries(K_LIST);
      },
    }
  );
}

/**
 * SOUND-521 — mark a single notification UNREAD (the inverse of useMarkRead).
 * Invalidates BOTH the unread count and the inbox list: the badge must drop/rise
 * immediately, and the list re-sorts because the backend orders unread-first.
 */
export function useMarkUnread() {
  const { authUser, companyId } = useAuthCtx();
  const qc = useQueryClient();
  return useMutation(
    (notificationId) =>
      makeApiRequest("post", apiRoutes.markNotificationUnread(notificationId), authUser, companyId),
    {
      onSuccess: () => {
        qc.invalidateQueries(K_UNREAD);
        qc.invalidateQueries(K_LIST);
      },
    }
  );
}

export function useMarkAllRead() {
  const { authUser, companyId } = useAuthCtx();
  const qc = useQueryClient();
  return useMutation(
    () => makeApiRequest("post", apiRoutes.markAllNotificationsRead, authUser, companyId),
    {
      onSuccess: () => {
        qc.invalidateQueries(K_UNREAD);
        qc.invalidateQueries(K_LIST);
      },
    }
  );
}

export function useNotificationPreferences({ enabled = true } = {}) {
  const { authUser, companyId, ready } = useAuthCtx();
  return useQuery(
    K_PREFS,
    async () => {
      const res = await makeApiRequest(
        "get",
        apiRoutes.notificationPreferences,
        authUser,
        companyId
      );
      return res.data || { events: [], mutedAll: false };
    },
    { enabled: enabled && ready, staleTime: 60_000, refetchOnWindowFocus: false }
  );
}

export function useUpdateNotificationPreferences() {
  const { authUser, companyId } = useAuthCtx();
  const qc = useQueryClient();
  return useMutation(
    (body) => makeApiRequest("put", apiRoutes.notificationPreferences, authUser, companyId, body),
    { onSuccess: () => qc.invalidateQueries(K_PREFS) }
  );
}
